import { useState, useRef, useEffect, FormEvent } from 'react';

const eventTypes = [
  'Exclusive Wedding',
  'Corporate Gala',
  'VIP Birthday Celebration',
  'Luxury Cocktail Party',
  'Private Engagement Party',
  'Charity Ball',
  'Product Launch',
  'Exclusive Private Dining',
  'Other Premium Event',
];

const guestRanges = [
  'Up to 50 guests',
  '50 – 100 guests',
  '100 – 200 guests',
  '200 – 500 guests',
  '500+ guests',
];

const budgetRanges = [
  '$5,000 – $10,000',
  '$10,000 – $25,000',
  '$25,000 – $50,000',
  '$50,000 – $100,000',
  '$100,000+',
];

const services = [
  { id: 'flair_bartending', label: 'Flair Bartending' },
  { id: 'mixology_classes', label: 'Mixology Classes' },
  { id: 'equipment_hire', label: 'Equipment Hire' },
  { id: 'signature_cocktails', label: 'Signature Cocktail Menu' },
  { id: 'full_bar_setup', label: 'Full Bar Setup' },
  { id: 'staff_hire', label: 'Staff Hire' },
];

export default function VIPEnquiryForm() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [charCount, setCharCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const toggleService = (id: string) => {
    setSelectedServices(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      return;
    }
    setStatus('sending');
    try {
      const data = new FormData(form);
      const body = new URLSearchParams();
      data.forEach((val, key) => body.append(key, val as string));
      body.append('services_requested', selectedServices.join(', '));
      const res = await fetch('https://readdy.ai/api/form/d6krmqdv117fnkj2ggr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setSelectedServices([]);
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
    setTimeout(() => setStatus('idle'), 6000);
  };

  return (
    <div
      ref={ref}
      className={`relative transition-all duration-1000 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
    >
      {/* Outer glow border */}
      <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-600 opacity-60 blur-sm" />

      <div
        className="relative rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, #1a1208 0%, #2d1f0a 40%, #1a1208 100%)',
        }}
      >
        {/* Decorative gold shimmer top bar */}
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-amber-400 to-transparent" />

        {/* Subtle gold pattern overlay */}
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              #d4a017 0px,
              #d4a017 1px,
              transparent 1px,
              transparent 20px
            )`,
          }}
        />

        <div className="relative z-10 p-8 lg:p-10">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-500/60" />
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-vip-crown-2-fill text-amber-400 text-2xl" />
              </div>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-500/60" />
            </div>
            <p className="text-amber-400 text-xs tracking-[0.4em] uppercase font-medium mb-2">
              Exclusive Access
            </p>
            <h2
              className="text-3xl lg:text-4xl font-light text-amber-100 mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              VIP Event Enquiry
            </h2>
            <p className="text-amber-200/50 text-sm max-w-md mx-auto leading-relaxed">
              For premium packages and bespoke experiences. Our VIP concierge team will respond within 4 hours.
            </p>
          </div>

          <form id="vip-enquiry-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            {/* Name row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                  First Name <span className="text-amber-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  required
                  placeholder="Alexandra"
                  className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm placeholder-amber-700/50 focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition"
                />
              </div>
              <div>
                <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                  Last Name <span className="text-amber-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  required
                  placeholder="Beaumont"
                  className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm placeholder-amber-700/50 focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition"
                />
              </div>
            </div>

            {/* Contact row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                  Email Address <span className="text-amber-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="vip@example.com"
                  className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm placeholder-amber-700/50 focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition"
                />
              </div>
              <div>
                <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="04XX XXX XXX"
                  className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm placeholder-amber-700/50 focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition"
                />
              </div>
            </div>

            {/* Event type & date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                  Event Type <span className="text-amber-500">*</span>
                </label>
                <select
                  name="event_type"
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition cursor-pointer"
                >
                  <option value="" className="bg-stone-900">Select event type</option>
                  {eventTypes.map(t => (
                    <option key={t} value={t} className="bg-stone-900">{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                  Event Date <span className="text-amber-500">*</span>
                </label>
                <input
                  type="date"
                  name="event_date"
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition"
                />
              </div>
            </div>

            {/* Guest count & budget */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                  Expected Guests
                </label>
                <select
                  name="guest_count"
                  className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition cursor-pointer"
                >
                  <option value="" className="bg-stone-900">Select guest range</option>
                  {guestRanges.map(r => (
                    <option key={r} value={r} className="bg-stone-900">{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                  Estimated Budget
                </label>
                <select
                  name="budget_range"
                  className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition cursor-pointer"
                >
                  <option value="" className="bg-stone-900">Select budget range</option>
                  {budgetRanges.map(b => (
                    <option key={b} value={b} className="bg-stone-900">{b}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Services */}
            <div>
              <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-3">
                Services Required
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {services.map(s => {
                  const active = selectedServices.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleService(s.id)}
                      className={`px-3 py-2.5 rounded-xl text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                        active
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 border-amber-400 text-stone-900 shadow-lg shadow-amber-500/30'
                          : 'border-amber-700/40 bg-amber-950/30 text-amber-300/70 hover:border-amber-500/60 hover:text-amber-200'
                      }`}
                    >
                      {active && <i className="ri-check-line mr-1" />}
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Venue */}
            <div>
              <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                Venue / Location
              </label>
              <input
                type="text"
                name="venue"
                placeholder="e.g. Crown Palladium, Melbourne CBD"
                className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm placeholder-amber-700/50 focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition"
              />
            </div>

            {/* Vision / Message */}
            <div>
              <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                Your Vision{' '}
                <span className="normal-case text-amber-700/60 text-xs">
                  ({charCount}/500 characters)
                </span>
              </label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={4}
                onChange={e => setCharCount(e.target.value.length)}
                placeholder="Describe your dream event — the atmosphere, theme, special requests, or anything that makes your occasion unique..."
                className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm placeholder-amber-700/50 focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition resize-none"
              />
              {charCount > 450 && (
                <p className="text-amber-500 text-xs mt-1">{500 - charCount} characters remaining</p>
              )}
            </div>

            {/* How did you hear */}
            <div>
              <label className="block text-xs text-amber-400/80 uppercase tracking-widest mb-2">
                How Did You Hear About Us?
              </label>
              <select
                name="referral_source"
                className="w-full px-4 py-3.5 rounded-xl border border-amber-700/40 bg-amber-950/40 text-amber-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500/60 transition cursor-pointer"
              >
                <option value="" className="bg-stone-900">Select an option</option>
                <option value="Instagram" className="bg-stone-900">Instagram</option>
                <option value="Facebook" className="bg-stone-900">Facebook</option>
                <option value="Google Search" className="bg-stone-900">Google Search</option>
                <option value="Word of Mouth" className="bg-stone-900">Word of Mouth</option>
                <option value="Previous Client" className="bg-stone-900">Previous Client</option>
                <option value="Event Planner" className="bg-stone-900">Event Planner / Coordinator</option>
                <option value="Other" className="bg-stone-900">Other</option>
              </select>
            </div>

            {/* Divider */}
            <div className="h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" />

            {/* VIP perks note */}
            <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3.5">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                <i className="ri-star-fill text-amber-400 text-sm" />
              </div>
              <p className="text-amber-200/70 text-xs leading-relaxed">
                VIP enquiries receive a <strong className="text-amber-300">dedicated concierge</strong>, priority scheduling, complimentary consultation, and access to our exclusive premium packages not listed publicly.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={status === 'sending'}
              className="relative w-full py-4 rounded-xl font-semibold text-sm tracking-widest uppercase overflow-hidden group cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              style={{
                background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 30%, #f5c842 50%, #d4a017 70%, #b8860b 100%)',
                color: '#1a1208',
              }}
            >
              {/* Shimmer sweep */}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out pointer-events-none" />
              <span className="relative flex items-center justify-center gap-2">
                {status === 'sending' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Submitting VIP Request...
                  </>
                ) : (
                  <>
                    <i className="ri-vip-crown-2-fill" />
                    Submit VIP Enquiry
                  </>
                )}
              </span>
            </button>

            {status === 'success' && (
              <div className="flex items-center gap-3 bg-emerald-950/60 border border-emerald-500/30 rounded-xl px-5 py-4 text-sm">
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-checkbox-circle-fill text-emerald-400 text-lg" />
                </div>
                <div>
                  <p className="text-emerald-300 font-medium">VIP Enquiry Received!</p>
                  <p className="text-emerald-400/60 text-xs mt-0.5">Our concierge team will contact you within 4 hours.</p>
                </div>
              </div>
            )}
            {status === 'error' && (
              <div className="flex items-center gap-3 bg-rose-950/60 border border-rose-500/30 rounded-xl px-5 py-4 text-sm">
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-fill text-rose-400 text-lg" />
                </div>
                <p className="text-rose-300">Something went wrong. Please try again or call us directly.</p>
              </div>
            )}
          </form>

          {/* Bottom decorative line */}
          <div className="mt-8 flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-600/30" />
            <i className="ri-vip-diamond-fill text-amber-600/40 text-xs" />
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-600/30" />
          </div>
        </div>
      </div>
    </div>
  );
}
